const number = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);
const hasPillar = (row) =>
  row?.pillarId != null &&
  Number.isInteger(Number(row.pillarId)) &&
  Number(row.pillarId) > 0;

export function getPillarOptions(branches = []) {
  const audits = new Map();
  const pillars = new Map();
  branches.forEach((branch) =>
    (branch.audits || []).forEach((audit) => {
      const id = Number(audit.id);
      audits.set(id, {
        id,
        description: audit.description,
        date: audit.date,
        type: audit.type,
        branchName: branch.name,
      });
      const report = audit.reportGenerated || {};
      [
        ...(report.table2Pillars ?? report.table2 ?? []),
        ...(report.table1Pillars ?? report.table1 ?? []),
      ]
        .filter(hasPillar)
        .forEach((row) => {
          const pillarId = Number(row.pillarId);
          if (!pillars.has(pillarId))
            pillars.set(pillarId, {
              pillarId,
              referenceCode: row.referenceCode,
              description: row.description,
              presentAuditIds: new Set(),
            });
          pillars.get(pillarId).presentAuditIds.add(id);
        });
    }),
  );
  return [...pillars.values()]
    .map((pillar) => ({
      ...pillar,
      missingAudits: [...audits.values()].filter(
        (audit) => !pillar.presentAuditIds.has(audit.id),
      ),
    }))
    .sort(
      (a, b) =>
        String(a.referenceCode || "").localeCompare(
          String(b.referenceCode || ""),
          undefined,
          { numeric: true },
        ) || a.pillarId - b.pillarId,
    );
}

export function initializePillarSelection(data) {
  const key = [
    ...new Set(
      (data.branches || []).flatMap((branch) =>
        branch.audits.map((audit) => Number(audit.id)),
      ),
    ),
  ]
    .sort((a, b) => a - b)
    .join(",");
  return data.pillarSelectionKey === key &&
    Array.isArray(data.selectedPillarIds)
    ? data
    : {
        ...data,
        pillarSelectionKey: key,
        selectedPillarIds: getPillarOptions(data.branches).map(
          (pillar) => pillar.pillarId,
        ),
      };
}

// Keep the filtering contract in sync with backend services/branches/consolidatedPillars.js.
export function filterConsolidatedBranches(branches = [], selectedPillarIds) {
  const selected =
    selectedPillarIds === undefined
      ? null
      : new Set(selectedPillarIds.map(Number));
  return branches.map((branch) => ({
    ...branch,
    audits: (branch.audits || []).map((audit) => {
      // The preview's monthly aggregation mutates nested tables. Isolate its working copy.
      const report = JSON.parse(JSON.stringify(audit.reportGenerated || {}));
      const source1 = report.table1Pillars ?? report.table1 ?? [];
      const source2 = report.table2Pillars ?? report.table2 ?? [];
      if (selected) {
        const rows = source1.filter(
          (row) => hasPillar(row) && selected.has(Number(row.pillarId)),
        );
        const totals = {
          effStdTotal: 0,
          stdTotal: 0,
          devTotal: 0,
          naTotal: 0,
          totalPoints: 0,
        };
        rows.forEach((row) => {
          totals.effStdTotal += number(row.effStdPoints);
          totals.stdTotal += number(row.stdPoints);
          totals.devTotal += number(row.devPoints);
          totals.naTotal += number(row.naPoints);
        });
        totals.totalPoints = totals.stdTotal + totals.devTotal + totals.naTotal;
        totals.stdPercentage = totals.totalPoints
          ? totals.stdTotal / totals.totalPoints
          : 0;
        totals.devPercentage = totals.totalPoints
          ? totals.devTotal / totals.totalPoints
          : 0;
        totals.naPercentage = totals.totalPoints
          ? totals.naTotal / totals.totalPoints
          : 0;
        const denominator = totals.stdTotal + totals.devTotal;
        totals.totalPercentage = denominator
          ? totals.effStdTotal / denominator
          : 0;
        report.table1Pillars = [...rows, totals];
        report.table2Pillars = source2.filter(
          (row) => hasPillar(row) && selected.has(Number(row.pillarId)),
        );
        report.cover = { ...report.cover, score: totals.totalPercentage };
      } else {
        report.table1Pillars = source1;
        report.table2Pillars = source2;
      }
      report.table1 = report.table1Pillars;
      report.table2 = report.table2Pillars;
      return { ...audit, reportGenerated: report };
    }),
  }));
}
